import React, { useState } from 'react';
import { executeTrade } from '../../services/blockchainService';

interface SellModalProps {
  defaultTicker?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function SellModal({ defaultTicker = '', onClose, onSuccess }: SellModalProps) {
  const [ticker, setTicker] = useState(defaultTicker);
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState('');
  const [txHash, setTxHash] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const total = price * (parseFloat(quantity) || 0);

  const handleSell = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const qty = parseFloat(quantity);
    if (!ticker.trim()) { setError('Please enter a ticker symbol.'); return; }
    if (!qty || qty <= 0) { setError('Please enter a valid quantity.'); return; }
    setLoading(true);
    try {
      const result = await executeTrade({
        ticker: ticker.toUpperCase(),
        quantity: -qty,
        price,
        side: 'sell',
      });
      setTxHash(result.transactionHash);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Trade failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (txHash) {
    return (
      <div className="modal-overlay" role="dialog" aria-modal="true">
        <div className="modal-box">
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
          <h2 className="modal-title">✅ Sell Order Placed</h2>
          <p className="modal-success-msg">
            Your sell order for <strong>{ticker.toUpperCase()}</strong> was submitted.
          </p>
          <div className="tx-hash-box">
            <span className="tx-hash-label">Transaction Hash:</span>
            <span className="tx-hash-val">{txHash}</span>
          </div>
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Sell">
      <div className="modal-box">
        <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        <h2 className="modal-title">📉 Sell Security</h2>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSell} className="trade-form">
          <label className="auth-label">
            Ticker Symbol
            <input
              type="text"
              className="auth-input"
              value={ticker}
              onChange={e => setTicker(e.target.value.toUpperCase())}
              placeholder="e.g. AAPL, ETH"
              required
            />
          </label>
          <label className="auth-label">
            Price per unit ($)
            <input
              type="number"
              className="auth-input"
              value={price || ''}
              onChange={e => setPrice(parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </label>
          <label className="auth-label">
            Quantity
            <input
              type="number"
              className="auth-input"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              min="0"
              step="any"
              required
              placeholder="0"
            />
          </label>
          {price > 0 && parseFloat(quantity) > 0 && (
            <div className="trade-total">
              Proceeds: <strong>${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </div>
          )}
          <button type="submit" className="btn-primary btn-sell-action" disabled={loading}>
            {loading ? 'Processing…' : 'Sell'}
          </button>
        </form>
      </div>
    </div>
  );
}
